import {
    Box,
    Button,
    Card,
    Divider,
    LinearProgress,
    Tooltip,
    Typography
} from '@mui/material';
import React, {
    useMemo,
    useState
} from 'react';

import AddIcon from '@mui/icons-material/Add';
import { AssessmentScheduleForm } from "./AssessmentScheduleForm";
import { AssessmentScheduleListItem } from "./AssessmentScheduleListItem";
import { Contact } from "../../models";
import { Location } from "../../API";
import { useAssessments } from '../dashboard/contexts/AssessmentsContextProvider';
import { useLocations } from '../dashboard/contexts/LocationContextProvider';
import { useResourceNotificationSettings } from '../dashboard/contexts/ResourceNotificationSettingContextProvider';

export type ManageSchedulesProps = {
    contacts: Array<Contact>;
    setIsOpenCreateContactDialog: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ManageSchedules = (props: ManageSchedulesProps) => {
    const {
        contacts,
        setIsOpenCreateContactDialog
    } = props;
    const {
        fetchAssessmentScheduleStatus,
        assessmentSchedules,
        assessmentScheduleIdToResourceIdsMap,
        createAssessmentSchedule
    } = useAssessments();
    const { locations } = useLocations();
    const { missedAssessmentNotificationSettings } = useResourceNotificationSettings();

    const [creatingNewSchedule, setCreatingNewSchedule] = useState(false);

    const selectableLocations = useMemo(() => {
        const locationIdsWithAssessmentSchedule: Array<string> = Array.from(assessmentScheduleIdToResourceIdsMap?.values() ?? []).flat();
        return locations?.filter(location => !locationIdsWithAssessmentSchedule.includes(location.id)) ?? [];
    }, [assessmentScheduleIdToResourceIdsMap, locations]);

    const createNewScheduleTooltip = locations.length === 0 ?
        "Start by creating a location" :
        selectableLocations.length === 0 ?
            "All locations have an assessment schedule" :
            null;


    return (
        <>
            {creatingNewSchedule && contacts && setIsOpenCreateContactDialog && createAssessmentSchedule && (
                <Card sx={{ padding: 4, marginBottom: 2 }}>
                    <Typography variant="h5" fontWeight={500} marginBottom={2}>New Schedule</Typography>
                    <AssessmentScheduleForm
                        onSubmit={createAssessmentSchedule}
                        onCompleted={() => { setCreatingNewSchedule(false); }}
                        onCancel={() => setCreatingNewSchedule(false)}
                        contacts={contacts}
                        setIsOpenCreateContactDialog={setIsOpenCreateContactDialog}
                    />
                </Card>
            )}
            <Card sx={{ padding: 1.5 }}>
                <Box display="flex" justifyContent="space-between">
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h5" fontWeight={500}>Assessment Schedules</Typography>
                    </Box>
                    {createAssessmentSchedule && (
                        <Box display="flex" gap={2}>
                            <Tooltip title={createNewScheduleTooltip} enterTouchDelay={0}>
                                <span> {/* span is used to allow tooltip to work with disabled button */}
                                    {!creatingNewSchedule && (
                                        <Button
                                            onClick={() => setCreatingNewSchedule(true)}
                                            variant="outlined"
                                            color="inherit"
                                            disabled={!!createNewScheduleTooltip}
                                            startIcon={<AddIcon fontSize="small" />}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Create Schedule
                                        </Button>)}
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                <Box display="flex" flexDirection="column" gap={1} p={{ xs: 0.5, sm: 1 }} minHeight="150px">
                    {fetchAssessmentScheduleStatus === "loading" && <LinearProgress />}
                    {fetchAssessmentScheduleStatus === "error" && <Typography>Error fetching assessment schedules</Typography>}
                    {fetchAssessmentScheduleStatus === "success" && assessmentSchedules && (
                        assessmentSchedules.length === 0 ? (
                            <Box display="flex" p={2} justifyContent="center" alignItems="center">
                                <Typography>No assessment schedules found.</Typography>
                            </Box>
                        ) : (
                            assessmentSchedules.map((assessmentSchedule, index) => {
                                const locationIdsUnderSchedule = assessmentScheduleIdToResourceIdsMap?.get(assessmentSchedule.id) ?? [];
                                const locationsUnderSchedule = locationIdsUnderSchedule
                                    .map(locationId => locations?.find(location => location.id === locationId))
                                    .filter(location => !!location) as Array<Location>;
                                const missedAssessmentNotificationSetting = missedAssessmentNotificationSettings?.find(setting => setting.resourceId === assessmentSchedule.id);
                                return (
                                    <Box key={assessmentSchedule.id} display="flex" flexDirection="column" gap={0.25}>
                                        {assessmentScheduleIdToResourceIdsMap && (
                                            <AssessmentScheduleListItem
                                                assessmentSchedule={assessmentSchedule}
                                                associatedLocations={locationsUnderSchedule}
                                                contacts={contacts}
                                                setIsOpenCreateContactDialog={setIsOpenCreateContactDialog}
                                                missedNotificationSetting={missedAssessmentNotificationSetting}
                                            />
                                        )}
                                        {index !== assessmentSchedules.length - 1 && <Divider />}
                                    </Box>
                                );
                            })
                        )
                    )}
                </Box>
            </Card>
        </>
    );
};